import { useEffect, useEffectEvent, useRef } from 'react';

/** After this many automatic loads the visitor presses the button — so the footer stays reachable. */
export const AUTO_LOAD_PAGES = 3;

/**
 * Loads the next page when `sentinel` scrolls near the viewport — at most `AUTO_LOAD_PAGES` times per
 * result set, after which the "Load more" button takes over. Without IntersectionObserver the button
 * is simply the only way, which is fine.
 */
export function useAutoLoadMore(options: {
  sentinel: React.RefObject<Element | null>;
  enabled: boolean;
  /** Changes whenever the result set changes (resets the automatic-load budget). */
  resultKey: string;
  onLoad: () => void;
}): void {
  const { sentinel, enabled, resultKey } = options;
  const loaded = useRef({ key: resultKey, count: 0 });
  const load = useEffectEvent(() => {
    if (loaded.current.key !== resultKey) loaded.current = { key: resultKey, count: 0 };
    if (loaded.current.count >= AUTO_LOAD_PAGES) return;
    loaded.current.count += 1;
    options.onLoad();
  });

  useEffect(() => {
    const element = sentinel.current;
    if (!enabled || !element || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) load();
      },
      { rootMargin: '0px 0px 600px 0px' },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [sentinel, enabled, resultKey]);
}
